import { rebuildMonthlyStats } from "../utils/rebuildMonthlyStats";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Receipt, BadgeCheck, AlertTriangle, Sigma, RefreshCw } from "lucide-react";
import { getDocs, collection, limit, orderBy, query } from "firebase/firestore";
import { usePanelTheme } from "../context/themeContext";
import { db } from "../firebase";
import { getMonthlyStats, monthKeyFromDate } from "../services/statsService";
import { getCache, setCache } from "../utils/cache";
import { tData } from "../i18n";
import DashboardCharts from "../components/DashboardCharts";
import OverdueWarningPanel from "../components/OverdueWarningPanel";
import StatCard from "../components/StatCard";
import UIButton from "../components/UIButton";
import PanelCard from "../components/PanelCard";
import PageShell from "../components/PageShell";
import SectionHeading from "../components/SectionHeading";

const DASHBOARD_CACHE_PREFIX = "bauheld:dashboard:";
const STATS_TTL = 5 * 60 * 1000;
const LIST_TTL = 2 * 60 * 1000;

function formatMoney(value, currency = "EUR") {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function formatDate(value) {
  if (!value) return "—";
  if (value?.toDate) return value.toDate().toLocaleDateString("de-DE");
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("de-DE");
}

async function getLatestDocsCached(cacheKey, collectionName, force = false) {
  if (!force) {
    const cached = getCache(cacheKey, LIST_TTL);
    if (cached) return cached;
  }

  const snap = await getDocs(
    query(collection(db, collectionName), orderBy("createdAt", "desc"), limit(4))
  );

  const rows = snap.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  }));

  setCache(cacheKey, rows);
  return rows;
}

function FeedCard({
  title,
  rows,
  currency = "EUR",
  noData = "No data",
  amountKey = "amount",
  titleResolver,
  subResolver,
  currentTheme,
  theme,
}) {
  return (
    <PanelCard className="min-w-0 overflow-hidden p-4 sm:p-5">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-base font-black sm:text-lg">{title}</h3>
        <div
          className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] ${currentTheme.surfaceSoft}`}
        >
          Live
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {rows.length ? (
          rows.map((row) => (
            <div
              key={row.id}
              className={`min-w-0 overflow-hidden rounded-2xl border p-3 shadow-[0_12px_30px_rgba(15,23,42,0.04)] sm:p-4 ${
                theme === "dark"
                  ? "border-slate-700 bg-slate-900/60"
                  : "border-slate-200 bg-slate-50/80"
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate font-semibold">{titleResolver(row)}</div>
                  <div className={`mt-1 text-xs sm:text-sm ${currentTheme.muted}`}>
                    {subResolver(row)}
                  </div>
                </div>
                <div className="shrink-0 text-right text-sm font-black sm:text-base">
                  {formatMoney(row[amountKey] || row.grandTotal || row.total || 0, currency)}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className={`py-8 text-sm ${currentTheme.muted}`}>{noData}</div>
        )}
      </div>
    </PanelCard>
  );
}

export default function Dashboard({ language = "de" }) {
  const { currentTheme, theme } = usePanelTheme();
  const langPack = tData?.[language] || tData?.de || {};
  const monthKey = monthKeyFromDate();

  const labels = {
    title: langPack.dashboardTitle || langPack.dashboard || "Dashboard",
    subtitle:
      langPack.dashboardOptimizedSubtitle ||
      "Professional financial overview using only real Firebase data.",
    thisMonth: langPack.thisMonth || "This month",
    invoiced: langPack.dashboardInvoiced || "Invoiced",
    paidInInvoices: langPack.dashboardPaidInInvoices || "Paid",
    openInvoices: langPack.dashboardOpenInvoices || "Open invoices",
    income: langPack.income || "Income",
    expenses: langPack.expenses || "Expenses",
    payments: langPack.payments || "Payments",
    profit: langPack.dashboardProfit || "Result",
    lastInvoices: langPack.dashboardLastInvoices || "Latest invoices",
    lastIncome: langPack.dashboardLastIncome || "Latest income",
    lastExpenses: langPack.dashboardLastExpenses || "Latest expenses",
    refresh: langPack.invoiceRefresh || langPack.refresh || "Refresh",
    noData: langPack.noData || "No data",
    loading: langPack.loading || "Loading...",
    chartsTitle: langPack.dashboardChartsTitle || "Charts and trends",
    chartsSubtitle:
      langPack.dashboardChartsSubtitle ||
      "Charts are built only from real Firebase statistics.",
    overdueTitle: langPack.dashboardOverdueTitle || "Overdue invoices",
    overdueSubtitle:
      langPack.dashboardOverdueSubtitle ||
      "Immediate warning for invoices that need follow-up.",
  };

  const translateCategory = (categoryName) => {
    if (!categoryName) return null;
    if (langPack[categoryName]) return langPack[categoryName];

    const categoryDict = {
      sq: {
        Salary: "Pagë",
        Tools: "Vegla Pune",
        Other: "Të tjera",
        Material: "Materiale",
        Rent: "Qira",
        Taxes: "Tatime",
        Software: "Softuerë",
      },
      de: {
        Salary: "Gehalt",
        Tools: "Werkzeuge",
        Other: "Sonstiges",
        Material: "Material",
        Rent: "Miete",
        Taxes: "Steuern",
        Software: "Software",
      },
    };

    return categoryDict[language]?.[categoryName] || categoryName;
  };

  const [stats, setStats] = useState(null);
  const [latestInvoices, setLatestInvoices] = useState([]);
  const [latestIncome, setLatestIncome] = useState([]);
  const [latestExpenses, setLatestExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshTimestamp, setRefreshTimestamp] = useState(Date.now());

  const loadDashboard = useCallback(
    async (force = false) => {
      setLoading(true);
      try {
        if (force) {
          setRefreshTimestamp(Date.now());
        }

        const statsCacheKey = `${DASHBOARD_CACHE_PREFIX}stats:${monthKey}`;
        const cachedStats = !force ? getCache(statsCacheKey, STATS_TTL) : null;

        const statsPromise = cachedStats
          ? Promise.resolve(cachedStats)
          : getMonthlyStats(monthKey).then((data) => {
              setCache(statsCacheKey, data);
              return data;
            });

        const [statsData, invoicesData, incomeData, expensesData] = await Promise.all([
          statsPromise,
          getLatestDocsCached(`${DASHBOARD_CACHE_PREFIX}latest:invoices`, "invoices", force),
          getLatestDocsCached(`${DASHBOARD_CACHE_PREFIX}latest:income`, "income", force),
          getLatestDocsCached(`${DASHBOARD_CACHE_PREFIX}latest:expenses`, "expenses", force),
        ]);

        setStats(statsData || null);
        setLatestInvoices(invoicesData || []);
        setLatestIncome(incomeData || []);
        setLatestExpenses(expensesData || []);
      } catch (error) {
        console.error("Error loading dashboard:", error);
      } finally {
        setLoading(false);
      }
    },
    [monthKey]
  );

  useEffect(() => {
    setLoading(true);
    const currentMonthKey = monthKeyFromDate();

    rebuildMonthlyStats(currentMonthKey)
      .then(() => loadDashboard(true))
      .catch((error) => {
        console.error("Automatic recalc failed:", error);
        loadDashboard(true);
      });
  }, [loadDashboard]);

  const computedProfit = useMemo(() => {
    if (!stats) return 0;
    return (
      Number(stats.incomeTotal || 0) -
      Number(stats.expensesTotal || 0) -
      Number(stats.paymentsTotal || 0)
    );
  }, [stats]);

  const currency = stats?.currency || "EUR";

  return (
    <PageShell>
      <PanelCard className="min-w-0 overflow-hidden px-4 py-5 sm:px-6 sm:py-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="min-w-0">
            <div
              className={`inline-flex rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] ${currentTheme.surfaceSoft}`}
            >
              {labels.thisMonth}: {monthKey}
            </div>

            <p className={`mt-3 max-w-3xl text-sm ${currentTheme.muted}`}>
              {labels.subtitle}
            </p>
          </div>

          <div className="flex w-full items-center gap-3 xl:w-auto">
            <UIButton
              type="button"
              onClick={() => loadDashboard(true)}
              theme={theme}
              variant="primary"
              size="lg"
              className="w-full sm:w-auto"
            >
              <RefreshCw className="h-4 w-4" />
              {labels.refresh}
            </UIButton>
          </div>
        </div>
      </PanelCard>

      {loading ? (
        <PanelCard className="p-10 text-center text-sm">
          <span className={currentTheme.muted}>{labels.loading}</span>
        </PanelCard>
      ) : (
        <>
          <div className="grid min-w-0 grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard
              title={labels.invoiced}
              value={formatMoney(stats?.invoicesTotal || 0, currency)}
              subtitle={labels.thisMonth}
              icon={Receipt}
              accent="slate"
            />
            <StatCard
              title={labels.paidInInvoices}
              value={formatMoney(stats?.invoicesPaid || 0, currency)}
              subtitle={labels.income}
              icon={BadgeCheck}
              accent="emerald"
            />
            <StatCard
              title={labels.openInvoices}
              value={formatMoney(stats?.invoicesOpen || 0, currency)}
              subtitle={labels.overdueTitle}
              icon={AlertTriangle}
              accent="amber"
            />
            <StatCard
              title={labels.profit}
              value={formatMoney(computedProfit, currency)}
              subtitle={labels.payments}
              icon={Sigma}
              accent="blue"
            />
          </div>

          <div className="min-w-0 space-y-3 overflow-hidden">
            <SectionHeading
              title={labels.chartsTitle}
              subtitle={labels.chartsSubtitle}
            />
            <PanelCard className="min-w-0 overflow-hidden p-4 sm:p-5">
              <div className="min-w-0 overflow-hidden">
                <DashboardCharts
                  key={`charts-${refreshTimestamp}`}
                  language={language}
                  stats={stats}
                />
              </div>
            </PanelCard>
          </div>

          <div className="grid min-w-0 grid-cols-1 gap-4 xl:grid-cols-3">
            <FeedCard
              title={labels.lastInvoices}
              rows={latestInvoices}
              currency={currency}
              noData={labels.noData}
              amountKey="grandTotal"
              currentTheme={currentTheme}
              theme={theme}
              titleResolver={(row) => row.invoiceNumber || row.number || "—"}
              subResolver={(row) =>
                `${row.clientName || row.companyName || "—"} • ${formatDate(
                  row.invoiceDate || row.date || row.createdAt
                )}`
              }
            />

            <FeedCard
              title={labels.lastIncome}
              rows={latestIncome}
              currency={currency}
              noData={labels.noData}
              amountKey="amount"
              currentTheme={currentTheme}
              theme={theme}
              titleResolver={(row) => translateCategory(row.clientName || row.source) || "—"}
              subResolver={(row) =>
                `${row.notes || row.description || "—"} • ${formatDate(
                  row.date || row.createdAt
                )}`
              }
            />

            <FeedCard
              title={labels.lastExpenses}
              rows={latestExpenses}
              currency={currency}
              noData={labels.noData}
              amountKey="amount"
              currentTheme={currentTheme}
              theme={theme}
              titleResolver={(row) => translateCategory(row.category) || row.title || "—"}
              subResolver={(row) =>
                `${row.notes || row.description || "—"} • ${formatDate(
                  row.date || row.createdAt
                )}`
              }
            />
          </div>

          <div className="min-w-0 space-y-3 overflow-hidden">
            <SectionHeading
              title={labels.overdueTitle}
              subtitle={labels.overdueSubtitle}
            />
            <PanelCard className="min-w-0 overflow-hidden p-4 sm:p-5">
              <div className="min-w-0 overflow-hidden">
                <OverdueWarningPanel
                  key={`overdue-${refreshTimestamp}`}
                  language={language}
                />
              </div>
            </PanelCard>
          </div>
        </>
      )}
    </PageShell>
  );
}
